import { Component, OnInit } from '@angular/core';
import { UserDetailsService } from 'src/app/services/user-details.service';
import { NewFeedbackGroupService } from 'src/app/services/new-feedback-group.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ProfileDetailComponent } from '../profile-detail/profile-detail.component';
import { GeoLocation } from 'src/app/services/user/user-enum';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-calibrated-report',
  templateUrl: './calibrated-report.component.html',
  styleUrls: ['./calibrated-report.component.scss'],
})
export class CalibratedReportComponent implements OnInit {
  selected: number;
  nodata: Boolean = true;
  years = [];
  userDtao = [];
  isAll: Boolean = true;
  isOpen: Boolean = false;
  isClose: Boolean = false;
  selectedHrLocation = '';
  locationArray = [];
  searchText: string;
  filteredList = [];
  loginUserInfo;
  isSuperAdmin: Boolean = false;
  data = [];
  isLocationDropDownTouched: Boolean = false;
  loggedInUserLocation: string;

  constructor(
    private feedbackgroupservice: NewFeedbackGroupService,
    private userDetailService: UserDetailsService,
    private dialogue: MatDialog,
    private toastService: ToastrService
  ) {}

  ngOnInit(): void {
    this.isSuperAdmin = this.userDetailService.hasRole(['ROLE_SUPER_ADMIN']);
    this.loginUserInfo = this.userDetailService.getUserDetails();
    this.loggedInUserLocation = this.loginUserInfo.geoLocation;
    this.selectedHrLocation =
      this.loginUserInfo.geoLocation != null
        ? this.loginUserInfo.geoLocation
        : '';
    if (this.isSuperAdmin && this.selectedHrLocation == '') {
      this.selectedHrLocation = 'INDIA';
    }
    this.getCalendarNewDate();
    this.getAllEmployeeForCalibratedScore();
    this.updateLocationArray();
    this.applyDefaultFilter();
  }

  applyDefaultFilter() {
    this.isLocationDropDownTouched = true; // Ensure filter is applied
    if (this.isAll) {
      this.All();
    } else if (this.isOpen) {
      this.Open();
    } else {
      this.Close();
    }
  }

  updateLocationArray() {
    if (this.isSuperAdmin) {
      this.locationArray = Object.keys(GeoLocation);
    } else {
      if (this.loggedInUserLocation === 'INDIA') {
        this.locationArray.push('INDIA', 'AUSTRALIA');
      } else if (this.loggedInUserLocation === 'USA') {
        this.locationArray.push('USA');
      } else if (this.loggedInUserLocation === 'AUSTRALIA') {
        this.locationArray.push('AUSTRALIA');
      }
    }
  }

  getCalendarNewDate() {
    this.selected = new Date().getFullYear();
    for (let year = this.selected; year >= 2022; year--) {
      this.years.push(year);
    }
  }

  onViewReportee(id) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '100%';
    dialogConfig.height = '95%';
    dialogConfig.maxWidth = '95%';
    dialogConfig.data = {
      userId: id,
      viewer: 'HR',
    };
    const dialogRef = this.dialogue.open(ProfileDetailComponent, dialogConfig);
  }

  getAllEmployeeForCalibratedScore() {
    if (this.loggedInUserLocation) {
      this.feedbackgroupservice
        .getdirectreporteebyyearforHr(this.selected)
        .subscribe({
          next: (res) => {
            this.data = res.body;
  
            // Check if response body is valid and assign to userDtao
            if (Array.isArray(res.body)) {
              this.userDtao = res.body.map((emp) => ({
                firstName: emp.firstName ?? '',
                lastName: emp.lastName ?? '',
                ...emp, // Retain other fields in emp
              }));
            } else {
              console.warn('Unexpected response format:', res.body);
              this.userDtao = [];
            }
  
            // Apply default filter and user-defined filters
            this.applyDefaultFilter();
            this.applyFilter();
  
            // Update UI flags
            this.nodata = this.userDtao.length === 0;
  
            // Handle other states
            if (this.isAll) { 
                    
              this.All(); 
            } else if (this.isOpen) {
             
              this.Open();
            } else {
              
              this.Close();
            }
          },
          error: (err) => {
            console.error('Error fetching employees:', err);
            this.toastService.error('Failed to fetch employee data. Please try again.');
          },
        });
    } else {
      this.toastService.warning(
        'Your geo-location is not configured, please contact the administrator.'
      );

    }
  }
  applyFilter() {
    // Ensure searchText is always a string (fallback to an empty string if undefined or null)
    const filterValue = (this.searchText ?? '').toLowerCase().trim();
  
    // Filter the list
    this.filteredList = this.userDtao.filter((emp) => {
      const firstName = (emp.firstName ?? '').toLowerCase();
      const lastName = (emp.lastName ?? '').toLowerCase();
      const fullName = `${firstName}${lastName}`;
      
      // Match against first name, last name, or full name
      return (
        firstName.includes(filterValue) ||
        lastName.includes(filterValue) ||
        fullName.includes(filterValue)
      );
    });
  
    // Sort the filtered list
    this.filteredList.sort((a, b) => {
      const aFirstName = (a.firstName ?? '').toLowerCase();
      const bFirstName = (b.firstName ?? '').toLowerCase();
      const aStartsWithSearch = aFirstName.startsWith(filterValue);
      const bStartsWithSearch = bFirstName.startsWith(filterValue);
  
      // Prioritize items where the first name starts with the search text
      if (aStartsWithSearch && !bStartsWithSearch) {
        return -1; // a comes first
      }
      if (!aStartsWithSearch && bStartsWithSearch) {
        return 1; // b comes first
      }
  
      // If neither or both start with the search text, sort alphabetically by full name
      const aFullName = `${aFirstName}${(a.lastName ?? '').toLowerCase()}`;
      const bFullName = `${bFirstName}${(b.lastName ?? '').toLowerCase()}`;
      return aFullName.localeCompare(bFullName);
    });
  }  

  downloadExcel() {
    const XLSX = require('xlsx');

    const formattedData = this.userDtao.map((item) => ({
      'Employee Name': `${item.firstName} ${item.lastName}`,
      'Feedback Overall Score': item.totalScore,
      'Employee ID': item.employeeId,
      'Job Title': item.jobTitle,
      'Feedback Status': item.closed ? 'Closed' : 'Open',
      'Geo Location': item.userLocation,
      Capability: item.capability.replaceAll('_', ' '),
      'Career Manager': item.careerManager,
    }));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Feedback Data');

    XLSX.writeFile(
      workbook,
      'FeedbackData-' + this.selected + `-${this.selectedHrLocation}` + '.xlsx'
    );
  }

  changeYear(e: any) {
    this.selected = e.value;
    this.getAllEmployeeForCalibratedScore();
  }

  Open() {
    this.userDtao = this.data.filter((obj) => {
      return (
        (this.selectedHrLocation.toString() == obj.userLocation.toString() ||
          !this.isLocationDropDownTouched) &&
        !obj.closed
      );
    });
    this.filteredList=this.userDtao;
    this.isOpen = true;
    this.isClose = false;
    this.isAll = false;
  }

  Close() {
    const closedFeedbacks= this.data.filter((obj) => {
      return (
        (this.selectedHrLocation.toString() == obj.userLocation.toString() ||
          !this.isLocationDropDownTouched) &&
        obj.closed
      );
    });
    if (closedFeedbacks.length > 0) {
      this.userDtao = closedFeedbacks;
      this.filteredList=closedFeedbacks;
      this.nodata = false;
    } else {
        this.userDtao=[];
        this.filteredList=[];
        this.nodata = true; 
        this.toastService.warning('No closed feedback available for the selected location and year.');
    }

    this.isOpen = false;
    this.isClose = true;
    this.isAll = false;
  }

  All() {
    this.userDtao = this.data.filter((obj) => {
      return (
        this.selectedHrLocation.toString() == obj.userLocation.toString() ||
        !this.isLocationDropDownTouched
      );
    });
    this.isOpen = false;
    this.isClose = false;
    this.isAll = true;
  }

  changeLocation(e) {
    this.isLocationDropDownTouched = true;
    this.selectedHrLocation = e.value.toUpperCase();
    if (this.isOpen == true) {
      this.userDtao = this.data.filter((obj) => {
        return e.value.toString() == obj.userLocation.toString() && !obj.closed;
      });
    } else if (this.isClose == true) {
      this.userDtao = this.data.filter((obj) => {
        return e.value.toString() == obj.userLocation.toString() && obj.closed;
      });
    } else {
      this.userDtao = this.data.filter((obj) => {
        return e.value.toString() == obj.userLocation.toString();
      });
    }
  }

  getValueByKey(key: string): string | undefined {
    const enumValues = Object.values(GeoLocation) as string[];
    const enumKeys = Object.keys(GeoLocation);
    const index = enumKeys.indexOf(key);

    if (index !== -1) {
      return enumValues[index];
    }

    return undefined;
  }
}
